import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, Lock, CheckCircle2 } from 'lucide-react';
import { collectionService } from '../../services/collectionService';
import { secretKeyService } from '../../services/secretKeyService';
import { CaptchaData } from '../../types';
import { useToast } from '../../context/ToastContext';

interface DeleteConfirmModalProps {
  collectionId: string;
  collectionName: string;
  authorName: string;
  secretKeyHash?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  collectionId,
  collectionName,
  authorName,
  secretKeyHash,
  onClose,
  onSuccess,
}) => {
  const toast = useToast();
  const [secretKeyInput, setSecretKeyInput] = useState('');
  const [captcha, setCaptcha] = useState<CaptchaData | null>(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [loadingCaptcha, setLoadingCaptcha] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    secretKeyService.getSecretKeyForCollection({ id: collectionId, secretKeyHash }).then((key) => {
      if (key) {
        setSecretKeyInput(key);
      } else {
        setSecretKeyInput('');
      }
    });
    fetchCaptcha();
  }, [collectionId, secretKeyHash]);

  const fetchCaptcha = async () => {
    setLoadingCaptcha(true);
    try {
      const data = await collectionService.generateCaptcha();
      setCaptcha(data);
    } catch (e) {
      console.error(e);
      setError('Không thể khởi tạo CAPTCHA. Vui lòng thử lại.');
    } finally {
      setLoadingCaptcha(false);
    }
  };

  const handleDelete = async () => {
    if (!secretKeyInput.trim()) {
      setError('Vui lòng nhập Secret Key của bộ sưu tập này.');
      toast.warning('Vui lòng nhập Secret Key của bộ sưu tập này.');
      return;
    }
    if (!captchaAnswer.trim()) {
      setError('Vui lòng trả lời câu hỏi CAPTCHA.');
      toast.warning('Vui lòng trả lời câu hỏi CAPTCHA.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Client-side SHA-256 hash
      const secretKeyHash = await secretKeyService.hashKey(secretKeyInput.trim());

      await collectionService.deleteCollection({
        collectionId,
        secretKeyHash,
        captcha: {
          nonce: captcha?.nonce || '',
          questionId: captcha?.questionId || '',
          answer: captchaAnswer.trim(),
        },
      });

      // Remove from localStorage if saved
      secretKeyService.removeKey(collectionId);
      toast.success('Xóa bộ sưu tập thành công!');
      onSuccess();
    } catch (err: any) {
      const msg = err.message || 'Lỗi khi xóa bộ sưu tập.';
      setError(msg);
      toast.error(msg);
      // Refresh captcha on error
      fetchCaptcha();
      setCaptchaAnswer('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '0.625rem',
          maxWidth: '480px',
          width: '100%',
          padding: '1.5rem',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          position: 'relative',
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            right: '1rem',
            top: '1rem',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#666',
          }}
        >
          <X size={20} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '1rem' }}>
          <div
            style={{
              padding: '0.5rem',
              borderRadius: '50%',
              backgroundColor: '#fee2e2',
              color: 'var(--accent-danger)',
              display: 'flex',
            }}
          >
            <AlertTriangle size={24} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700, color: 'var(--text-main)' }}>
              Xác Nhận Xóa Bộ Sưu Tập
            </h3>
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8125rem', color: '#666' }}>
              Quyền quản trị của tác giả <strong>{authorName}</strong>
            </p>
          </div>
        </div>

        <p style={{ fontSize: '0.875rem', color: '#444', lineHeight: 1.5, marginBottom: '1rem' }}>
          Bạn có chắc chắn muốn xóa bộ sưu tập <strong>"{collectionName}"</strong>? Thao tác này sẽ xóa vĩnh viễn file và không thể khôi phục.
        </p>

        {error && (
          <div
            style={{
              backgroundColor: '#fee2e2',
              color: 'var(--accent-danger)',
              padding: '0.625rem 0.75rem',
              borderRadius: '0.375rem',
              fontSize: '0.8125rem',
              marginBottom: '1rem',
            }}
          >
            {error}
          </div>
        )}

        {/* Step 1: Secret Key Input */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.375rem', color: '#333' }}>
            Secret Key (Mã Khôi Phục Cá Nhân):
          </label>
          <div style={{ position: 'relative' }}>
            <Lock size={16} style={{ position: 'absolute', left: '0.625rem', top: '50%', transform: 'translateY(-50%)', color: '#888' }} />
            <input
              type="text"
              placeholder="Dán mã UUID Secret Key tại đây..."
              value={secretKeyInput}
              onChange={(e) => setSecretKeyInput(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem 0.625rem 0.5rem 2.25rem',
                borderRadius: '0.375rem',
                border: '1px solid var(--border-light)',
                fontSize: '0.875rem',
                boxSizing: 'border-box',
              }}
            />
          </div>
          {secretKeyInput && (
            <p style={{ fontSize: '0.75rem', color: '#16a34a', margin: '0.25rem 0 0 0', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <CheckCircle2 size={12} /> Tự động lấy Mã Khôi Phục từ lưu trữ thiết bị này.
            </p>
          )}
        </div>

        {/* Step 2: CAPTCHA */}
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.375rem', color: '#333' }}>
            Xác nhận CAPTCHA bảo mật:
          </label>
          {loadingCaptcha ? (
            <div style={{ fontSize: '0.8125rem', color: '#666' }}>Đang tải câu hỏi CAPTCHA...</div>
          ) : (
            <div>
              <p style={{ fontSize: '0.875rem', color: '#1f2937', fontWeight: 500, margin: '0 0 0.5rem 0', backgroundColor: '#f3f4f6', padding: '0.5rem', borderRadius: '0.25rem' }}>
                {captcha?.questionText}
              </p>
              <input
                type="text"
                placeholder="Nhập câu trả lời..."
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.625rem',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--border-light)',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '0.5rem 0.875rem',
              borderRadius: '0.375rem',
              border: '1px solid var(--border-light)',
              backgroundColor: '#ffffff',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Hủy bỏ
          </button>

          <button
            onClick={handleDelete}
            disabled={submitting || loadingCaptcha}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              border: 'none',
              backgroundColor: 'var(--accent-danger)',
              color: '#ffffff',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: submitting ? 'wait' : 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'Đang xóa...' : 'Xác Nhận Xóa Vĩnh Viễn'}
          </button>
        </div>
      </div>
    </div>
  );
};
